import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Layout, Avatar, Tooltip, Button } from 'antd';
import {
  UserOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AgentConfigService from '../services/AgentConfigService';
import EnvVersionLabel from './Envversionlabel';
import TabNavigation from './TabNavigation';

const { Title, Text } = Typography;
const { Header, Sider, Content } = Layout;

/**
 * HCCConfigHome - "Configurations and Rules" page for the HCC Workspace
 *
 * Shows a set of static configuration/rules cards. Each card navigates to a
 * predefined route. No dynamic counts or remote data are fetched for the cards
 * themselves — the page is purely a launcher.
 *
 * Scaffolded to match AgentHCCHome.jsx (sidebar, header, gradient background)
 * and styled tiles to match AgentColabHome.jsx (Card + Title/Text body).
 *
 * NOTE for integration:
 *   - Add a new feature key (e.g. "HCC_CONFIG") to your features list so
 *     TabNavigation renders the new tab.
 *   - Insert the new key into the fallback `tabOrder` arrays where applicable.
 *   - Register a route in AppRouter for this component, e.g. `/hcc-config`.
 *   - Update TabNavigation.jsx to render a tab for "HCC_CONFIG" that
 *     navigates to that route.
 *
 * @param {Object} props
 * @param {string} props.username - Current user's username
 * @param {string} props.sessionId - Current session ID
 * @param {string} props.userRole - Current user's role
 * @param {Array} props.userFeatures - User's available features/tabs
 * @param {Array} props.tabOrder - Order of tabs to display
 * @param {function} props.onLogout - Logout handler function
 */
const HCCConfigHome = ({ username, sessionId, userRole, userFeatures, tabOrder, onLogout }) => {
  const navigate = useNavigate();

  // State for features (same fallback pattern as AgentHCCHome)
  const [features, setFeatures] = useState(userFeatures || ['HUB', 'COLAB', 'HCC', 'HCC_CONFIG']);
  const [localTabOrder, setLocalTabOrder] = useState(
    tabOrder || ['HUB', 'HCC', 'HCC_CONFIG', 'COLAB', 'MCP', 'COUNSEL']
  );

  // Refresh features on navigation — mirrors AgentHCCHome behavior so the
  // TabNavigation strip stays in sync if the user lands here directly.
  useEffect(() => {
    const refreshFeatures = async () => {
      try {
        const mainApiUrl = await AgentConfigService.getMainApiUrl();
        const response = await fetch(`${mainApiUrl}/features`, {
          headers: { 'Authorization': `Bearer ${sessionId}` },
          credentials: 'include'
        });

        if (response.ok) {
          const { features: serverFeatures } = await response.json();
          setFeatures(serverFeatures);
        }

        if (response.tab_order) {
          setLocalTabOrder(response.tab_order);
        }
      } catch (error) {
        console.warn('Failed to refresh features, using fallback:', error);
        setFeatures(['HUB', 'COLAB', 'HCC', 'HCC_CONFIG']);
      }
    };

    if (sessionId && !userFeatures) {
      refreshFeatures();
    }
  }, [sessionId, userFeatures]);

  /**
   * Render a static configuration tile.
   *
   * @param {string} title       - Card title (shown bold, in HCC navy).
   * @param {string} description - Short description, 1–3 sentences.
   * @param {string} route       - React Router path to navigate to on click.
   */
  const renderConfigTile = (title, description, route) => {
    return (
      <Card
        hoverable
        style={{
          height: '100%',
          cursor: 'pointer',
          border: '1px solid #d9d9d9',
          borderRadius: '8px'
        }}
        bodyStyle={{ padding: '24px' }}
        onClick={() => navigate(route)}
      >
        <div style={{ minHeight: '160px', display: 'flex', flexDirection: 'column' }}>
          <Title
            level={4}
            style={{
              marginBottom: '12px',
              color: '#162349',
              fontWeight: 'bold'
            }}
          >
            {title}
          </Title>

          <Text
            style={{
              marginBottom: '16px',
              color: '#666',
              flex: 1
            }}
          >
            {description}
          </Text>

          <div style={{ marginTop: 'auto' }}>
            <Text
              style={{
                fontSize: '13px',
                color: '#162349',
                fontWeight: 'bold'
              }}
            >
              Open →
            </Text>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* ---------- Left Sidebar (mirrors AgentHCCHome) ---------- */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10
        }}
      >
        <div
          style={{
            display: 'flex',
            background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            alignItems: 'center',
            padding: '20px 0'
          }}
        >
          {/* Top spacer */}
          <div></div>


          {/* Bottom user/logout/version */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{
                  backgroundColor: 'white',
                  color: '#162349',
                  cursor: 'pointer',
                  border: '2px solid white'
                }}
                icon={<UserOutlined />}
              />
            </Tooltip>

            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>

            <EnvVersionLabel
              style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '12px',
                fontWeight: 'Bold'
              }}
            />
          </div>
        </div>
      </Sider>

      {/* ---------- Main Layout ---------- */}
      <Layout style={{ marginLeft: '60px' }}>
        {/* Top Header with TabNavigation (mirrors AgentHCCHome) */}
        <Header
          style={{
            background: '#162349',
            padding: '8px 24px 0 24px',
            height: 'auto',
            color: 'white'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0' }}>
            <TabNavigation
              features={features}
              activeTab="HCC_CONFIG"
              tabOrder={localTabOrder}
            />
          </div>
        </Header>

        {/* Main Content */}
        <Content
          style={{
            padding: '24px',
            background:
              'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)'
          }}
        >
          {/* Page Title */}
          <Title level={1} style={{ marginBottom: '40px', fontSize: '28px',  }}>
            Configurations and Rules
          </Title>

          {/* ----- Section 1: Configurations ----- */}
          <div style={{ marginBottom: '48px' }}>
            <Title level={2} style={{ marginBottom: '24px', fontSize: '22px' }}>
              Configurations
            </Title>

            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} md={8}>
                {renderConfigTile(
                  'Configure Chart Section Variations',
                  'Maintain how various sections appear within charts.',
                  '/hcc-config/sections'
                )}
              </Col>

              <Col xs={24} sm={12} md={8}>
                {renderConfigTile(
                  'Agentic Runtime Configuration',
                  'Maintain decide to infividual processors on/off, define LLM usage',
                  '/hcc-config/runtime'
                )}
              </Col>
            </Row>
          </div>

          {/* ----- Section 2: Rules ----- */}
          <div style={{ marginBottom: '48px' }}>
            <Title level={2} style={{ marginBottom: '24px', fontSize: '22px' }}>
              Rules
            </Title>

            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} md={8}>
                {renderConfigTile(
                  'Chronic Conditions',
                  'Broad definition of chronic conditions',
                  '/hcc-config/chronic'
                )}
              </Col>

              <Col xs={24} sm={12} md={8}>
                {renderConfigTile(
                  'Focused Condition Extractions',
                  'Replace this description with what this rule set covers. Keep it to 1–3 sentences so the card height stays consistent.',
                  '/hcc-config/chronic-focused'
                )}
              </Col>

              <Col xs={24} sm={12} md={8}>
                {renderConfigTile(
                  'Code Corrections',
                  'Replace this description with what this rule set covers. Keep it to 1–3 sentences so the card height stays consistent.',
                  '/hcc-config/code-corrections'
                )}
              </Col>

              <Col xs={24} sm={12} md={8}>
                {renderConfigTile(
                  'Corroboration Rules',
                  'Replace this description with what this rule set covers. Keep it to 1–3 sentences so the card height stays consistent.',
                  '/hcc-config/corroborations'
                )}
              </Col>

              <Col xs={24} sm={12} md={8}>
                {renderConfigTile(
                  'Evidence Search',
                  'Replace this description with what this rule set covers. Keep it to 1–3 sentences so the card height stays consistent.',
                  '/hcc-config/evidence-search'
                )}
              </Col>

              <Col xs={24} sm={12} md={8}>
                {renderConfigTile(
                  'Specificity Resolution',
                  'Rules to resolve specificity  among sibling codes.',
                  '/hcc-config/specificity'
                )}
              </Col>



            </Row>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default HCCConfigHome;